import {Modal, ModalContent, Text, styled, Box} from "@reside/ui";
import {useQuery} from "react-query";
import {useChangeHistoryModalContext} from "./ChangeHistoryModalContext";
import {AdmissionsService} from "../../../../services/AdmissionsService";
import {getAdmissionResidentName} from "../../helper";
import {parseISO} from "date-fns";
import {formatInTimeZone} from "date-fns-tz";
import {AdmissionStatusTransition} from "@reside/reside-api-admission";
import {mapTransitionStatus} from "../../../../utils/helpers/mapTransitionStatus";

export const ChangeHistoryModal = () => {
  const {admission, closeModal} = useChangeHistoryModalContext();

  return (
    <ChangeHistoryModalRenderer
      isOpen={Boolean(admission)}
      onClose={() => {
        closeModal();
      }}
    />
  );
};

type ChangeHistoryModalContentProps = Readonly<{
  isOpen: boolean;

  isLoading?: boolean;
  onClose: () => void;
}>;

type TableDataProps = Readonly<{
  width?: number;
}>;

const TableData = styled.td<TableDataProps>`
  padding: 8px 0;
  width: ${(props: TableDataProps) =>
    props.width !== null ? `${props.width}%` : "20%"};
`;

export const ChangeHistoryModalRenderer = ({
  isOpen,
  onClose,
}: ChangeHistoryModalContentProps) => {
  const {admission} = useChangeHistoryModalContext();

  const {data, isLoading} = useQuery({
    queryFn: () => AdmissionsService.transitions(admission.id),
    enabled: isOpen && Boolean(admission),
  });

  const admissionUser = admission ? getAdmissionResidentName(admission) : "";

  const renderContent = data?.data?.length ? (
    <div>
      <Box gap={6}>
        <Text color="gray100" fontWeight="bold" fontSize={14} as="p">
          Admission ID: <Text color="primary">{admission?.code}</Text>
        </Text>
        <Text color="gray100" fontWeight="bold" fontSize={14} as="p">
          Current Status:{" "}
          <Text color="primary">
            {mapTransitionStatus(
              admission?.status as AdmissionStatusTransition.StatusEnum,
            )}
          </Text>
        </Text>
      </Box>
      <table>
        <thead>
          <tr>
            <th>TIMESTAMP (UTC)</th>
            <th>STATUS</th>
            <th>USER</th>
            <th>IP ADDRESS</th>
            <th>USER AGENT</th>
          </tr>
        </thead>
        <tbody>
          {data.data.map((_admission, ix) => (
            <tr key={ix}>
              <TableData>
                {formatInTimeZone(
                  parseISO(_admission.created),
                  "UTC",
                  "MMM-dd-yyyy HH:mm:ss",
                )}
              </TableData>
              <TableData width={15}>
                <Text>{mapTransitionStatus(_admission.status)}</Text>
                <Text as="p" fontSize={1} color="gray100">
                  {_admission?.description}
                </Text>
              </TableData>
              <TableData width={25}>{_admission.username ?? ""}</TableData>
              <TableData>{_admission.ipAddress || "***"}</TableData>
              <TableData>{_admission.userAgent ?? ""}</TableData>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <Text fontSize={16}>No transitions found</Text>
  );

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={{content: {maxWidth: "80%"}}}
      title={`${admissionUser} Document Change History`}
    >
      {() => {
        return (
          <ModalContent>
            {isLoading ? <p>...</p> : <>{renderContent}</>}
          </ModalContent>
        );
      }}
    </Modal>
  );
};
